import { ATTACHMENTS_BUCKET } from "../commons/attachments";
import { getSupabaseClient } from "./supabase";
import { getStoredFilePath } from "./storagePath";

const signedFiles = new Map<
  string,
  { expires: number; result: Promise<string> }
>();
export const clearSignedFileCache = () => signedFiles.clear();

export const signStoredSource = async (src: unknown): Promise<unknown> => {
  const path = getStoredFilePath(
    src,
    import.meta.env.VITE_SUPABASE_URL,
    ATTACHMENTS_BUCKET,
  );
  if (!path) return src;
  const client = getSupabaseClient();
  const { data: session } = await client.auth.getSession();
  if (!session.session) return "";
  const key = `${session.session.user.id}:${path}`;
  for (const [cachedKey, entry] of signedFiles) {
    if (entry.expires <= Date.now()) signedFiles.delete(cachedKey);
  }
  const cached = signedFiles.get(key);
  if (cached && cached.expires > Date.now()) return cached.result;
  const entry = {
    expires: Date.now() + 240000,
    result: Promise.resolve(""),
  };
  entry.result = client.storage
    .from(ATTACHMENTS_BUCKET)
    .createSignedUrl(path, 300)
    .then(({ data, error }) => {
      if (error || !data) {
        if (signedFiles.get(key) === entry) signedFiles.delete(key);
        return "";
      }
      return data.signedUrl;
    })
    .catch(() => {
      if (signedFiles.get(key) === entry) signedFiles.delete(key);
      return "";
    });
  signedFiles.set(key, entry);
  return entry.result;
};

export const signStoredRecord = async <T extends Record<string, any>>(
  record: T,
): Promise<T> => {
  const result: Record<string, any> = { ...record };
  await Promise.all(
    ["avatar", "logo", "lightModeLogo", "darkModeLogo"].map(async (key) => {
      const value = record[key];
      if (typeof value === "string")
        result[key] = await signStoredSource(value);
      else if (value && typeof value.src === "string")
        result[key] = {
          ...value,
          src: await signStoredSource(value.src),
        };
    }),
  );
  if (Array.isArray(record.attachments))
    result.attachments = await Promise.all(
      record.attachments.map((file: any) =>
        typeof file?.src === "string"
          ? signStoredRecord({ avatar: file }).then((r) => r.avatar)
          : file,
      ),
    );
  if (record.expense_snapshot)
    result.expense_snapshot = await signStoredRecord(record.expense_snapshot);
  if (record.config) result.config = await signStoredRecord(record.config);
  return result as T;
};
